/**
 * @fileoverview Main Application Component - ArchonPM
 * 
 * @description
 * Root component for ArchonPM - Comprehensive seismic project cost management system.
 * Manages application routing, authentication flow, onboarding process, and global state.
 * 
 * **Key Features:**
 * - User authentication and authorization
 * - Active project context management
 * - Database connection monitoring with retry mechanism
 * - Protected routes with role-based access control
 * 
 * **Architecture:**
 * - AuthProvider: User authentication context
 * - ActiveProjectProvider: Current project context
 * - AppRoutes: Modular routing system (see /routes folder)
 * - NavigationBar: Top navigation
 * - Footer: Database status indicator
 * 
 * @component App
 * @version 3.0.0
 * @author Paul Lachi
 * @date 2026-01-08
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// =========================================================================
// IMPORTS - API
// =========================================================================
import { api } from './api';

// =========================================================================
// IMPORTS - Context Providers
// =========================================================================
import { AuthProvider, useAuth } from './context/AuthContext';
import { ActiveProjectProvider, useActiveProject } from './context/ActiveProjectContext';
import { CompanyProjectsProvider } from './context/CompanyProjectsContext';
import { SidebarProvider, useSidebar, SIDEBAR_FLOATING_TOGGLE_RESERVE_PX } from './context/SidebarContext';
import { ProductTourProvider } from './context/ProductTourContext';
import { LanguageProvider } from './context/LanguageContext';
import { MenuAccessProvider } from './context/MenuAccessContext';
import { AccessManifestProvider } from './context/AccessManifestContext';
import { ChatNotificationProvider } from './context/ChatNotificationContext';
import { ThemeProvider, useTheme } from './theme';

// =========================================================================
// IMPORTS - Layout Components
// =========================================================================
import NavigationBar from './components/layout/NavigationBar';
import Footer from './components/layout/Footer';

// =========================================================================
// IMPORTS - Modular Routes
// =========================================================================
import AppRoutes from './routes';
import useVersionCheck from './hooks/useVersionCheck';
import { useProfileLandingRedirect } from './hooks/useProfileLandingRedirect';
import { useIsMobileLayout } from './hooks/useMediaQuery';
import UpdateBanner from './components/ui/UpdateBanner';
import DemoEnvironmentBanner from './components/ui/DemoEnvironmentBanner';
import ErrorBoundary from './components/ErrorBoundary';
import { ConfirmProvider } from './components/ui/modals/ConfirmProvider';
import { PromptProvider } from './components/ui/modals/PromptProvider';

// =========================================================================
// IMPORTS - Effects
// =========================================================================
// LiquidEther waves — mounted via ArchonBackgroundLayer (portal on body)
import ArchonBackgroundLayer from './components/effects/ArchonBackgroundLayer';

// =========================================================================
// IMPORTS - Styles and Assets
// =========================================================================
import './App.css';
import './custom.css';
import MainLogo from './assets/MainLogo.png';
import MainLogoSmall from './assets/MainLogosv.png';

// =========================================================================
// MAIN APP CONTENT COMPONENT
// =========================================================================

/**
 * AppContent Component
 * 
 * Main application content with routing, onboarding, and database monitoring.
 * Wrapped by AuthProvider and ActiveProjectProvider contexts.
 * 
 * @component
 */
function AppContent() {
  // =========================================================================
  // HOOKS - Context Access
  // =========================================================================
  const { user } = useAuth();
  const { theme, backgroundEffect } = useTheme();
  const { setActiveProject } = useActiveProject();
  const { sidebarHidden } = useSidebar();
  const { updateAvailable, forceRefresh } = useVersionCheck();
  const isSmallScreen = useIsMobileLayout();
  const navigate = useNavigate();
  const location = useLocation();

  useProfileLandingRedirect();

  // After a version-update reload, clean the _v cache-bust param from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('_v')) {
      params.delete('_v');
      const cleanSearch = params.toString() ? '?' + params.toString() : '';
      const cleanPath = window.location.pathname + cleanSearch;
      window.history.replaceState(null, '', cleanPath);
    }
  }, [location.pathname]);

  // Stable key: AuthContext often replaces `user` with a new object (same id/username).
  // Depending on `[user]` retriggered DB polling and other effects every refresh.
  const dbStatusUserKey =
    user == null ? '' : `${String(user.id ?? '')}:${user.username ?? ''}`;

  // =========================================================================
  // STATE - Database Connection
  // =========================================================================
  const [dbStatus, setDbStatus] = useState('disconnected');
  // Show top DB-disconnected banner only for ~5s, then rely on footer
  const [showDbDisconnectedBanner, setShowDbDisconnectedBanner] = useState(true);

  // =========================================================================
  // DATABASE CONNECTION MONITORING
  // Only check DB status when user is authenticated (not on login page)
  // =========================================================================

  const checkDbStatus = async () => {
    // Don't check DB status on public routes - no need and avoids re-renders on login
    if (!user) {
      return;
    }
    try {
      const res = await api.get('/status');
      setDbStatus(res.data.status === "ok" ? "connected" : "disconnected");
    } catch {
      setDbStatus('disconnected');
    }
  };

  useEffect(() => {
    // Only run DB status checks if user is authenticated
    if (!user) {
      return;
    }
    // Defer first check to avoid long task on initial mount (Violation messages)
    const t = setTimeout(checkDbStatus, 200);
    // Optimized: Check every 30s instead of 10s, and pause when tab is hidden
    let interval = setInterval(checkDbStatus, 30000);
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        checkDbStatus();
        interval = setInterval(checkDbStatus, 30000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(t);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dbStatusUserKey]); // identity by id/username, not `user` reference

  // When we go disconnected, show top banner for 5s then hide; when reconnected, reset for next time
  useEffect(() => {
    if (dbStatus === 'disconnected') {
      setShowDbDisconnectedBanner(true);
      const t = setTimeout(() => setShowDbDisconnectedBanner(false), 5000);
      return () => clearTimeout(t);
    }
    if (dbStatus === 'connected') {
      setShowDbDisconnectedBanner(true);
    }
  }, [dbStatus]);

  const isPublicRoute = location.pathname === '/' || location.pathname === '/login';
  /** Fixed bottom UI or long mobile forms: hide app footer so actions stay visible (no overlap). */
  // Both /drilling/crew-report/:id (new) and the legacy /operations/drilling/crew-report/:id
  // need the fullscreen layout; the wildcard match for /crew-report-field must NOT trigger it.
  const crewReportFormPath =
    (location.pathname.startsWith('/drilling/crew-report/') &&
      !location.pathname.startsWith('/drilling/crew-report-field')) ||
    (location.pathname.startsWith('/operations/drilling/crew-report/') &&
      !location.pathname.startsWith('/operations/drilling/crew-report-field'));
  /** Preplot / acquisition grid maps — footer overlaps map controls (zoom badge, Leaflet UI). */
  const isPreplotMapRoute =
    /\/preplot(?:\/|$)/.test(location.pathname) ||
    /\/projects\/[^/]+\/scope(?:\/|$)/.test(location.pathname) ||
    location.pathname === '/survey/map' ||
    location.pathname === '/recording/map' ||
    location.pathname === '/projects/combined-progress';
  const hideAppFooter =
    location.pathname.startsWith('/platform') ||
    location.pathname.startsWith('/operations/jm') ||
    location.pathname.includes('/operations/fuel-control/supervisor') ||
    location.pathname.includes('/drilling/progress-map') ||
    location.pathname.includes('/drilling/crew-report-field') ||
    // Legacy paths kept while back-compat redirects in QhseOperationsRoutes.js are live.
    location.pathname.includes('/operations/field-operations') ||
    location.pathname.includes('/operations/drilling/crew-report-field') ||
    crewReportFormPath ||
    isPreplotMapRoute;

  // =========================================================================
  // STYLE CONFIGURATION
  // =========================================================================

  const appStyle = {
    width: '100vw',
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    position: 'relative',
  };

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div style={appStyle} className="archon-app-shell">
      {!isPublicRoute && (
        <ArchonBackgroundLayer theme={theme} effect={backgroundEffect} />
      )}

      {/* Centered Logo Circle (only for authenticated routes) — ring is semi-transparent */}
      {!isPublicRoute && (
        <div className="archon-center-logo">
          <div
            className={`archon-center-logo__ring ${
              isSmallScreen ? 'archon-center-logo__ring--mobile' : 'archon-center-logo__ring--desktop'
            }`}
          >
            <img
              src={isSmallScreen ? MainLogoSmall : MainLogo}
              alt="Axiom Logo"
              className="archon-center-logo__img"
            />
          </div>
        </div>
      )}
      
      {/* Navigation Bar (only for authenticated routes) */}
      {!isPublicRoute && <NavigationBar />}

      <div className="archon-ui-column">
        {/* Update Available Banner */}
        {updateAvailable && <UpdateBanner onReload={forceRefresh} />}

        {!isPublicRoute && <DemoEnvironmentBanner />}

        {/* Database Status Banner — subtle, 5s only; then status lives in footer */}
        {!isPublicRoute && dbStatus === 'disconnected' && showDbDisconnectedBanner && (
          <div style={{
            background: '#2563eb',
            color: '#fff',
            padding: '8px 12px',
            textAlign: 'center',
            fontSize: '0.9em',
            zIndex: 9999
          }}>
            Warning: DB disconnected… Offline mode
          </div>
        )}

        {/* Main Content Container — indent when desktop floating nav toggle is visible */}
        <main
          data-tour="main-content"
          className="archon-main-scroll"
          style={{
            paddingLeft:
              !isPublicRoute && !isSmallScreen && sidebarHidden
                ? SIDEBAR_FLOATING_TOGGLE_RESERVE_PX
                : undefined,
            boxSizing: 'border-box',
          }}
        >
          <AppRoutes />
        </main>

        {/* Application Footer (only for authenticated routes; hidden on Fuel Supervisor — fixed save bar) */}
        {!isPublicRoute && !hideAppFooter && <Footer dbStatus={dbStatus} />}
      </div>
    </div>
  );
}

/**
 * App Component - Root Application Wrapper
 * 
 * Provides the context provider hierarchy:
 * 1. LanguageProvider - Internationalization
 * 2. AuthProvider - Authentication and user session management
 * 3. ActiveProjectProvider - Active project state management
 * 4. AccessManifestProvider - Capability manifest (must be above CompanyProjectsProvider)
 * 5. CompanyProjectsProvider - Company projects availability
 * 6. MenuAccessProvider - Menu access permissions
 * 7. ChatNotificationProvider - Operations chat notifications
 * 8. SidebarProvider - Sidebar collapse state
 * 9. AppContent - Main application routing and UI
 */
function App() {
  return (
    <ErrorBoundary>
    <ConfirmProvider>
    <PromptProvider>
    <LanguageProvider>
      <AuthProvider>
        <ThemeProvider>
        <ActiveProjectProvider>
          {/* AccessManifest must wrap CompanyProjects (CompanyProjects uses useAccessManifest). */}
          <AccessManifestProvider>
            <CompanyProjectsProvider>
              <MenuAccessProvider>
                <ChatNotificationProvider>
                  <SidebarProvider>
                    <ProductTourProvider>
                      <AppContent />
                    </ProductTourProvider>
                  </SidebarProvider>
                </ChatNotificationProvider>
              </MenuAccessProvider>
            </CompanyProjectsProvider>
          </AccessManifestProvider>
        </ActiveProjectProvider>
        </ThemeProvider>
      </AuthProvider>
    </LanguageProvider>
    </PromptProvider>
    </ConfirmProvider>
    </ErrorBoundary>
  );
}

export default App;